import React, { useState } from 'react';
import { db } from '../../firebaseConfig';
import { collection, addDoc } from 'firebase/firestore';
import type { HelpdeskTicket, UserProfile, Organization } from '@types';

interface HelpdeskModalProps {
    isOpen: boolean;
    onClose: () => void;
    category: 'Industry Help and Support' | 'Application IT Help and Support';
    user: UserProfile | null;
    organization: Organization | null;
    onSubmitted?: () => void;
}

const HelpdeskModal: React.FC<HelpdeskModalProps> = ({ isOpen, onClose, category, user, organization, onSubmitted }) => {
    const [subject, setSubject] = useState('');
    const [priority, setPriority] = useState<HelpdeskTicket['priority']>('medium');
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !organization) {
            setError('User session not found. Please log in again.');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const now = new Date().toISOString();
            const ticketData: Omit<HelpdeskTicket, 'id'> = {
                organizationId: organization.id,
                userId: user.uid,
                userEmail: user.email,
                userName: user.displayName || 'Anonymous User',
                category,
                subject,
                priority,
                description,
                status: 'open',
                createdAt: now,
                updatedAt: now,
            };

            await addDoc(collection(db, 'helpdesk_tickets'), ticketData);
            setIsSuccess(true);
            onSubmitted?.();
        } catch (err: any) {
            console.error('Error submitting helpdesk ticket:', err);
            setError(err.message || 'Failed to submit ticket. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity duration-300">
                <div className="bg-slate-900 border border-emerald-500/30 rounded-2xl shadow-2xl w-full max-w-md p-8 text-center animate-in zoom-in duration-300">
                    <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-emerald-500/20">
                        <svg className="w-10 h-10 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">Ticket Submitted</h2>
                    <p className="text-slate-400 mb-8">
                        Your {category === 'Industry Help and Support' ? 'industry consultancy' : 'technical support'} request has been logged. Our team will review it shortly.
                    </p>
                    <button 
                        onClick={() => {
                            setIsSuccess(false);
                            setSubject('');
                            setDescription('');
                            onClose();
                        }}
                        className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 rounded-xl transition-all shadow-lg shadow-emerald-900/20"
                    >
                        Return to Platform
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div 
            className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity duration-300 cursor-pointer"
            onClick={onClose}
        >
            <div 
                className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col animate-in zoom-in duration-300 cursor-default"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-6 py-4 bg-slate-800/50 border-b border-slate-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${category === 'Industry Help and Support' ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'}`}>
                            {category === 'Industry Help and Support' ? (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                            ) : (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                            )}
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-white leading-tight">{category}</h3>
                            <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Support Request Form</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {error && (
                        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm flex items-center gap-3">
                            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            {error}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-slate-300">Subject</label>
                            <input 
                                type="text"
                                required
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="E.g., Assistance with SRM Workflow Architect"
                                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-slate-300">Priority Level</label>
                            <select 
                                value={priority}
                                onChange={(e) => setPriority(e.target.value as HelpdeskTicket['priority'])}
                                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all font-semibold"
                            >
                                <option value="low">Low — General Inquiry</option>
                                <option value="medium">Medium — Operational Need</option>
                                <option value="high">High — Urgent Assistance</option>
                                <option value="urgent">Urgent — Critical Blocker</option>
                            </select>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-slate-300">Detailed Description</label>
                        <textarea 
                            required
                            rows={5}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Please describe your request or technical issue in detail..."
                            className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all resize-none"
                        />
                    </div>

                    <div className="pt-4 flex items-center justify-between gap-4">
                        <div className="text-xs text-slate-500 italic max-w-xs">
                            Submitted as: <span className="text-slate-400">{user?.displayName || user?.email}</span>
                        </div>
                        <div className="flex gap-3">
                            <button 
                                type="button"
                                onClick={onClose}
                                className="px-6 py-3 rounded-xl font-semibold text-slate-300 hover:bg-slate-800 transition-all"
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit"
                                disabled={isSubmitting}
                                className={`px-8 py-3 rounded-xl font-bold text-white transition-all shadow-lg ${
                                    category === 'Industry Help and Support' 
                                        ? 'bg-blue-600 hover:bg-blue-500 shadow-blue-900/20' 
                                        : 'bg-purple-600 hover:bg-purple-500 shadow-purple-900/20'
                                } flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                        Submitting...
                                    </>
                                ) : 'Submit Ticket'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default HelpdeskModal;
